import { useCallback, useEffect, useMemo, useState } from 'react';

import type { Battle, MusicGenre, MyAccount, TrackPlatform } from '../types';
import { musicGenreLabel } from '../../shared/music-genres';
import { useMusicGenreOptions } from '../lib/music-genres';
import { BattleComments } from './BattleComments';
import { BattleVotePanel } from './BattleVotePanel';
import { SponsoredSlot } from './SponsoredSlot';

const platforms: Array<[TrackPlatform, string]> = [
  ['spotify', 'Spotify'],
  ['youtube', 'YouTube / YouTube Music'],
  ['apple_music', 'Apple Music'],
  ['soundcloud', 'SoundCloud'],
  ['deezer', 'Deezer'],
  ['tidal', 'Tidal'],
];

type BattleFilter = 'live' | 'waiting_opponent' | 'active' | 'closed';

type BattleDraft = {
  title: string;
  description: string;
  opponentArtisticName: string;
  genre: MusicGenre;
  platform: TrackPlatform;
  trackUrl: string;
};

const emptyDraft: BattleDraft = {
  title: '',
  description: '',
  opponentArtisticName: '',
  genre: 'rap',
  platform: 'spotify',
  trackUrl: '',
};

const battleDraftStorageKey = (userId: string) => `agam:battle-draft:v1:${userId}`;

type BattleFormErrors = Partial<Record<keyof BattleDraft, string>>;

function loadSavedDraft(userId: string): BattleDraft | null {
  try {
    const saved = window.localStorage.getItem(battleDraftStorageKey(userId));
    if (!saved) return null;
    return { ...emptyDraft, ...(JSON.parse(saved) as Partial<BattleDraft>) };
  } catch {
    return null;
  }
}

export function BattlesArena({ authenticated }: { authenticated: boolean }) {
  const genres = useMusicGenreOptions();
  const [battles, setBattles] = useState<Battle[]>([]);
  const [account, setAccount] = useState<MyAccount | null>(null);
  const [filter, setFilter] = useState<BattleFilter>('live');
  const [genreFilter, setGenreFilter] = useState<string>('all');
  const [artistQuery, setArtistQuery] = useState('');
  const [artistFilter, setArtistFilter] = useState('');
  const [sharedBattleSlug, setSharedBattleSlug] = useState(
    () => new URLSearchParams(window.location.search).get('battle') ?? '',
  );
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<BattleDraft>(emptyDraft);
  const [draftOwner, setDraftOwner] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<BattleFormErrors>({});
  const [joiningSlug, setJoiningSlug] = useState<string | null>(null);
  const [joinPlatform, setJoinPlatform] = useState<TrackPlatform>('spotify');
  const [joinUrl, setJoinUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const loadBattles = useCallback(async () => {
    setLoading(true);
    try {
      if (sharedBattleSlug) {
        const response = await fetch(`/api/v1/battles/${encodeURIComponent(sharedBattleSlug)}`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!response.ok) throw new Error('La batalla compartida ya no está disponible.');
        const body = (await response.json()) as { battle: Battle };
        setBattles([body.battle]);
        return;
      }

      const params = new URLSearchParams({ status: filter, limit: '18' });
      if (genreFilter !== 'all') params.set('genre', genreFilter);
      if (artistFilter) params.set('artist', artistFilter);
      const response = await fetch(`/api/v1/battles?${params.toString()}`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) throw new Error('No fue posible abrir la arena.');
      const body = (await response.json()) as { battles: Battle[] };
      setBattles(body.battles);
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : 'No fue posible abrir la arena.');
    } finally {
      setLoading(false);
    }
  }, [artistFilter, filter, genreFilter, sharedBattleSlug]);

  const loadAccount = useCallback(async () => {
    if (!authenticated) {
      setAccount(null);
      return;
    }
    const response = await fetch('/api/v1/me', { headers: { Accept: 'application/json' } });
    setAccount(response.ok ? ((await response.json()) as MyAccount) : null);
  }, [authenticated]);

  useEffect(() => {
    void loadBattles();
    const refresh = () => void loadBattles();
    window.addEventListener('agam:battle-created', refresh);
    return () => window.removeEventListener('agam:battle-created', refresh);
  }, [loadBattles]);

  useEffect(() => {
    void loadAccount();
    const refresh = () => void loadAccount();
    window.addEventListener('agam:profile-updated', refresh);
    return () => window.removeEventListener('agam:profile-updated', refresh);
  }, [loadAccount]);

  useEffect(() => {
    const userId = account?.user.id;
    if (!userId || draftOwner === userId) return;
    const saved = loadSavedDraft(userId);
    queueMicrotask(() => {
      if (saved) {
        setDraft(saved);
        setCreating(true);
      }
      setDraftOwner(userId);
    });
  }, [account?.user.id, draftOwner]);

  useEffect(() => {
    if (!draftOwner || draft === emptyDraft) return;
    window.localStorage.setItem(battleDraftStorageKey(draftOwner), JSON.stringify(draft));
  }, [draft, draftOwner]);

  const artistName =
    account?.profile?.accountType === 'artist' ? account.profile.artisticName : null;
  const isArtist = Boolean(artistName);
  const activeCount = useMemo(
    () => battles.filter((battle) => battle.status === 'active').length,
    [battles],
  );
  const markBattleFinalized = useCallback((slug: string, winnerSide: Battle['winnerSide']) => {
    setBattles((current) =>
      current.map((battle) =>
        battle.slug === slug ? { ...battle, status: 'closed', winnerSide } : battle,
      ),
    );
  }, []);

  async function apiAction(url: string, body?: unknown) {
    setBusy(true);
    setMessage(null);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      const result = (await response.json()) as { detail?: string };
      if (!response.ok) throw new Error(result.detail ?? 'No fue posible completar la acción.');
      await loadBattles();
      return true;
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : 'No fue posible completar la acción.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function createBattle(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    setFormErrors({});

    const localTrackError = validateTrackUrl(draft.platform, draft.trackUrl);
    if (localTrackError) {
      setFormErrors({ trackUrl: localTrackError });
      setBusy(false);
      return;
    }

    try {
      const response = await fetch('/api/v1/battles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          ...draft,
          opponentArtisticName: draft.opponentArtisticName || null,
        }),
      });
      const result = (await response.json()) as {
        detail?: string;
        errors?: Partial<Record<keyof BattleDraft, string[]>>;
      };
      if (!response.ok) {
        const errors = Object.fromEntries(
          Object.entries(result.errors ?? {}).map(([field, messages]) => [field, messages?.[0]]),
        ) as BattleFormErrors;
        if (Object.keys(errors).length > 0) setFormErrors(errors);
        else setMessage(result.detail ?? 'No fue posible publicar el reto.');
        return;
      }

      setDraft(emptyDraft);
      if (draftOwner) window.localStorage.removeItem(battleDraftStorageKey(draftOwner));
      setCreating(false);
      setMessage('Reto publicado en la arena.');
      await loadBattles();
    } catch {
      setMessage('No fue posible publicar el reto. Revisa tu conexión e inténtalo nuevamente.');
    } finally {
      setBusy(false);
    }
  }

  async function joinBattle(event: React.FormEvent, slug: string) {
    event.preventDefault();
    const joined = await apiAction(`/api/v1/battles/${slug}/join`, {
      platform: joinPlatform,
      trackUrl: joinUrl,
    });
    if (joined) {
      setJoiningSlug(null);
      setJoinUrl('');
      setMessage('Batalla aceptada. Ambos enlaces ya están activos.');
    }
  }

  async function cancelBattle(slug: string) {
    const cancelled = await apiAction(`/api/v1/battles/${slug}/cancel`);
    if (cancelled) setMessage('El reto pendiente fue cancelado.');
  }

  async function shareBattle(battle: Battle) {
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('battle', battle.slug);
    url.hash = 'arena';
    const shareData = {
      title: `${battle.title} · Arena Musical`,
      text:
        battle.kind === 'exhibition'
          ? `${battle.creatorArtist} vs. ${battle.challengedArtist}. Exhibición editorial: escucha y vota en Arena Musical.`
          : `${battle.creatorArtist} se enfrenta a ${battle.challengedArtist ?? 'un nuevo rival'}. Escucha y vota en Arena Musical.`,
      url: url.toString(),
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareData.url);
        setMessage('Enlace de la batalla copiado. Ya puedes compartirlo.');
      } else {
        window.prompt('Copia este enlace para compartir la batalla:', shareData.url);
      }
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') return;
      setMessage('No fue posible compartir. Inténtalo nuevamente.');
    }
  }

  function leaveSharedBattle() {
    const url = new URL(window.location.href);
    url.searchParams.delete('battle');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    setSharedBattleSlug('');
  }

  return (
    <section id="arena" className="mt-24 lg:mt-32">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <h2 className="text-3xl font-black tracking-tight text-white sm:text-5xl">Batallas</h2>
          <p className="mt-4 max-w-2xl text-sm leading-6 text-zinc-400">
            Cada canción permanece en la plataforma del artista. AGAM organiza el enfrentamiento;
            las reproducciones ocurren en el enlace oficial.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="rounded-full border border-fuchsia-300/15 bg-fuchsia-300/5 px-4 py-2 text-xs text-fuchsia-100">
            {activeCount} activas en pantalla
          </span>
          {isArtist && (
            <button
              type="button"
              onClick={() => setCreating((value) => !value)}
              className="rounded-full bg-white px-5 py-2.5 text-sm font-bold text-zinc-950 hover:bg-fuchsia-200"
            >
              {creating ? 'Cerrar formulario' : 'Publicar reto'}
            </button>
          )}
        </div>
      </div>

      <div className="mt-7">
        <SponsoredSlot slot="arena_center" />
      </div>

      {!authenticated && (
        <ArenaNotice>
          Inicia sesión y completa un perfil de artista para publicar o aceptar retos.
        </ArenaNotice>
      )}
      {authenticated && !isArtist && (
        <ArenaNotice>
          Puedes escuchar, votar y comentar como oyente. Las cuentas de oyente no publican ni
          aceptan retos.
        </ArenaNotice>
      )}

      {creating && isArtist && (
        <form
          onSubmit={createBattle}
          className="glass-panel mt-7 grid gap-4 rounded-[2rem] p-6 sm:grid-cols-2 sm:p-8"
        >
          <div className="sm:col-span-2">
            <p className="text-lg font-bold text-white">Nuevo reto de {artistName}</p>
            <p className="mt-1 text-xs text-zinc-500">
              Deja el rival vacío para que cualquier artista pueda aceptar.
            </p>
          </div>
          <FormField label="Título de la batalla" error={formErrors.title}>
            <input
              className={`field-input ${formErrors.title ? 'border-rose-400/60' : ''}`}
              value={draft.title}
              onChange={(event) => {
                setDraft({ ...draft, title: event.target.value });
                setFormErrors((current) => ({ ...current, title: undefined }));
              }}
              minLength={5}
              maxLength={100}
              required
              placeholder="Ej. Fuego del Pacífico"
            />
          </FormField>
          <FormField label="Rival directo (opcional)" error={formErrors.opponentArtisticName}>
            <input
              className={`field-input ${formErrors.opponentArtisticName ? 'border-rose-400/60' : ''}`}
              value={draft.opponentArtisticName}
              onChange={(event) => {
                setDraft({ ...draft, opponentArtisticName: event.target.value });
                setFormErrors((current) => ({ ...current, opponentArtisticName: undefined }));
              }}
              maxLength={60}
              placeholder="Nombre artístico exacto"
            />
          </FormField>
          <FormField label="Género para el ranking">
            <select
              className="field-input"
              value={draft.genre}
              onChange={(event) => setDraft({ ...draft, genre: event.target.value as MusicGenre })}
            >
              {genres.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Plataforma">
            <PlatformSelect
              value={draft.platform}
              onChange={(platform) => setDraft({ ...draft, platform })}
            />
          </FormField>
          <FormField label="Enlace oficial de tu canción" error={formErrors.trackUrl}>
            <input
              className={`field-input ${formErrors.trackUrl ? 'border-rose-400/60' : ''}`}
              value={draft.trackUrl}
              onChange={(event) => {
                setDraft({ ...draft, trackUrl: event.target.value });
                setFormErrors((current) => ({ ...current, trackUrl: undefined }));
              }}
              onBlur={() => {
                if (!draft.trackUrl) return;
                const error = validateTrackUrl(draft.platform, draft.trackUrl);
                setFormErrors((current) => ({ ...current, trackUrl: error ?? undefined }));
              }}
              type="url"
              required
              placeholder="https://open.spotify.com/track/..."
            />
          </FormField>
          <FormField label="Descripción (opcional)" error={formErrors.description} wide>
            <textarea
              className={`field-input min-h-24 resize-y ${formErrors.description ? 'border-rose-400/60' : ''}`}
              value={draft.description}
              onChange={(event) => {
                setDraft({ ...draft, description: event.target.value });
                setFormErrors((current) => ({ ...current, description: undefined }));
              }}
              maxLength={500}
              placeholder="Contexto del reto, temática o mensaje para la audiencia."
            />
          </FormField>
          <button
            disabled={busy}
            className="rounded-full bg-fuchsia-300 px-6 py-3 text-sm font-black text-zinc-950 disabled:opacity-50 sm:col-span-2"
          >
            {busy ? 'Publicando…' : 'Publicar en la arena'}
          </button>
        </form>
      )}

      {sharedBattleSlug ? (
        <div className="mt-8 flex items-center justify-between gap-4 rounded-2xl border border-fuchsia-300/15 bg-fuchsia-300/[0.05] px-5 py-4">
          <p className="text-sm text-fuchsia-100/80">Estás viendo una batalla compartida.</p>
          <button
            type="button"
            onClick={leaveSharedBattle}
            className="rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-white"
          >
            Ver todas
          </button>
        </div>
      ) : (
        <div className="glass-panel mt-8 rounded-[1.5rem] p-4 sm:p-5">
          <div className="flex flex-wrap gap-2" aria-label="Estado de las batallas">
            {(
              [
                ['live', 'Todas'],
                ['waiting_opponent', 'Buscan rival'],
                ['active', 'Activas'],
                ['closed', 'Finalizadas'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setFilter(value)}
                className={`rounded-full border px-4 py-2 text-xs transition ${filter === value ? 'border-white bg-white text-zinc-950' : 'border-white/10 text-zinc-400 hover:border-white/25'}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-[.75fr_1.25fr]">
            <label className="field-label">
              Género musical
              <select
                className="field-input mt-2"
                value={genreFilter}
                onChange={(event) => setGenreFilter(event.target.value)}
              >
                <option value="all">Todos los géneros</option>
                {genres.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                setArtistFilter(artistQuery.trim());
              }}
              className="field-label"
            >
              Buscar por artista
              <span className="mt-2 flex gap-2">
                <input
                  className="field-input"
                  value={artistQuery}
                  onChange={(event) => setArtistQuery(event.target.value)}
                  minLength={2}
                  maxLength={60}
                  placeholder="Nombre artístico"
                />
                <button className="rounded-xl bg-white px-4 text-sm font-bold text-zinc-950">
                  Buscar
                </button>
                {artistFilter && (
                  <button
                    type="button"
                    onClick={() => {
                      setArtistQuery('');
                      setArtistFilter('');
                    }}
                    className="rounded-xl border border-white/10 px-3 text-sm text-zinc-400"
                    aria-label="Limpiar artista"
                  >
                    ×
                  </button>
                )}
              </span>
            </form>
          </div>
        </div>
      )}

      {message && (
        <p
          role="status"
          className="mt-5 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-zinc-300"
        >
          {message}
        </p>
      )}

      {loading ? (
        <div className="glass-panel mt-6 rounded-[2rem] p-8 text-sm text-zinc-400">
          Cargando enfrentamientos…
        </div>
      ) : battles.length === 0 ? (
        <div className="mt-6 rounded-[2rem] border border-dashed border-white/12 p-10 text-center text-sm text-zinc-500">
          Todavía no hay batallas en este filtro. La primera puede empezar aquí.
        </div>
      ) : (
        <div className="mt-6 grid gap-5 lg:grid-cols-2">
          {battles.map((battle) => {
            const ownsBattle = artistName === battle.creatorArtist;
            const reservedForArtist =
              battle.challengeType === 'direct' && battle.challengedArtist === artistName;
            const canJoin =
              isArtist &&
              battle.status === 'waiting_opponent' &&
              !ownsBattle &&
              (battle.challengeType === 'open' || reservedForArtist);

            return (
              <article key={battle.id} className="battle-card rounded-[2rem] p-6 sm:p-7">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap gap-2">
                      <span
                        className={`battle-status ${battle.status === 'active' ? 'battle-status-active' : ''}`}
                      >
                        {battle.status === 'active'
                          ? 'En combate'
                          : battle.status === 'closed'
                            ? 'Finalizada'
                            : 'Buscando rival'}
                      </span>
                      {battle.kind === 'exhibition' && (
                        <span className="rounded-full border border-amber-200/25 bg-amber-200/10 px-3 py-1 text-[0.62rem] font-black uppercase tracking-[0.16em] text-amber-100">
                          Exhibición
                        </span>
                      )}
                    </div>
                    <h3 className="mt-4 text-2xl font-black tracking-tight text-white">
                      {battle.title}
                    </h3>
                  </div>
                  <span className="text-right text-[0.65rem] uppercase tracking-[0.18em] text-zinc-600">
                    {genreLabel(battle.genre)} ·{' '}
                    {battle.kind === 'exhibition'
                      ? 'Selección editorial'
                      : battle.challengeType === 'direct'
                        ? 'Reto directo'
                        : 'Reto abierto'}
                  </span>
                </div>

                {battle.kind === 'exhibition' && (
                  <div className="mt-4 rounded-2xl border border-amber-200/15 bg-amber-200/[0.04] px-4 py-3">
                    <p className="text-xs font-bold text-amber-100">
                      Batalla de exhibición creada por el admin
                    </p>
                    <p className="mt-1 text-xs text-amber-100/60">Esto no tiene ranking ELO</p>
                  </div>
                )}

                {battle.description && (
                  <p className="mt-3 text-sm leading-6 text-zinc-400">{battle.description}</p>
                )}

                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  {battle.entries.map((entry) => (
                    <a
                      key={entry.side}
                      href={entry.trackUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-2xl border border-white/10 bg-black/20 p-4 transition hover:border-fuchsia-300/40 hover:bg-fuchsia-300/5"
                    >
                      <span className="text-[0.62rem] font-bold uppercase tracking-[0.18em] text-zinc-600">
                        {entry.side === 'challenger' ? 'Retador' : 'Rival'} ·{' '}
                        {platformLabel(entry.platform)}
                      </span>
                      <strong className="mt-2 block text-base text-white">
                        {entry.artistName}
                      </strong>
                      <span className="mt-3 block text-xs text-fuchsia-200">
                        Escuchar canción ↗
                      </span>
                    </a>
                  ))}
                  {battle.status === 'waiting_opponent' && (
                    <div className="rounded-2xl border border-dashed border-white/12 p-4">
                      <span className="text-[0.62rem] font-bold uppercase tracking-[0.18em] text-zinc-600">
                        Rival
                      </span>
                      <strong className="mt-2 block text-base text-zinc-400">
                        {battle.challengedArtist ?? 'Lugar disponible'}
                      </strong>
                      <span className="mt-3 block text-xs text-zinc-600">
                        Falta el segundo enlace
                      </span>
                    </div>
                  )}
                </div>

                {battle.status === 'waiting_opponent' && (
                  <p className="mt-4 rounded-xl border border-white/8 bg-white/[0.025] px-4 py-3 text-xs leading-5 text-zinc-500">
                    El tiempo de 72 horas comenzará cuando un artista acepte el reto.
                  </p>
                )}

                <button
                  type="button"
                  onClick={() => void shareBattle(battle)}
                  className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-zinc-300 transition hover:border-fuchsia-300/30 hover:text-white"
                >
                  Compartir batalla ↗
                </button>

                {canJoin && joiningSlug !== battle.slug && (
                  <button
                    type="button"
                    onClick={() => setJoiningSlug(battle.slug)}
                    className="mt-5 w-full rounded-full border border-fuchsia-300/30 bg-fuchsia-300/10 px-5 py-3 text-sm font-bold text-fuchsia-100"
                  >
                    Aceptar con mi canción
                  </button>
                )}

                {joiningSlug === battle.slug && (
                  <form
                    onSubmit={(event) => joinBattle(event, battle.slug)}
                    className="mt-5 grid gap-3"
                  >
                    <PlatformSelect value={joinPlatform} onChange={setJoinPlatform} />
                    <input
                      className="field-input"
                      value={joinUrl}
                      onChange={(event) => setJoinUrl(event.target.value)}
                      type="url"
                      required
                      placeholder="Enlace oficial de tu canción"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        disabled={busy}
                        className="rounded-full bg-white px-4 py-2.5 text-sm font-bold text-zinc-950 disabled:opacity-50"
                      >
                        Confirmar
                      </button>
                      <button
                        type="button"
                        onClick={() => setJoiningSlug(null)}
                        className="rounded-full border border-white/10 px-4 py-2.5 text-sm text-zinc-400"
                      >
                        Cancelar
                      </button>
                    </div>
                  </form>
                )}

                {(battle.status === 'active' || battle.status === 'closed') && (
                  <>
                    <BattleVotePanel
                      battle={battle}
                      authenticated={authenticated}
                      onFinalized={markBattleFinalized}
                    />
                    <BattleComments battle={battle} authenticated={authenticated} />
                  </>
                )}

                {battle.kind === 'normal' &&
                  battle.status === 'closed' &&
                  battle.ratingStatus === 'no_contest' && (
                    <p className="mt-4 rounded-xl border border-amber-300/10 bg-amber-300/[0.035] px-4 py-3 text-xs text-amber-100/60">
                      Sin impacto en el ranking: no alcanzó los {battle.minimumValidVotes} votos
                      mínimos.
                    </p>
                  )}

                {ownsBattle && battle.status === 'waiting_opponent' && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => cancelBattle(battle.slug)}
                    className="mt-4 text-xs text-zinc-600 hover:text-rose-300 disabled:opacity-50"
                  >
                    Cancelar este reto
                  </button>
                )}
              </article>
            );
          })}
        </div>
      )}

      <div className="mt-6 rounded-2xl border border-dashed border-amber-200/15 px-5 py-4 text-center text-[0.68rem] uppercase tracking-[0.2em] text-amber-100/50">
        Espacio central de la arena · Patrocinio disponible
      </div>
    </section>
  );
}

function ArenaNotice({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-6 rounded-2xl border border-sky-300/10 bg-sky-300/[0.04] px-5 py-4 text-sm leading-6 text-sky-100/70">
      {children}
    </p>
  );
}

function FormField({
  label,
  error,
  wide,
  children,
}: {
  label: string;
  error?: string;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className={`field-label ${wide ? 'sm:col-span-2' : ''}`}>
      {label}
      {error && (
        <span role="alert" className="mt-2 block text-xs font-medium text-rose-300">
          {error}
        </span>
      )}
      <span className="mt-2 block">{children}</span>
    </label>
  );
}

function PlatformSelect({
  value,
  onChange,
}: {
  value: TrackPlatform;
  onChange: (value: TrackPlatform) => void;
}) {
  return (
    <select
      className="field-input"
      value={value}
      onChange={(event) => onChange(event.target.value as TrackPlatform)}
    >
      {platforms.map(([platform, label]) => (
        <option key={platform} value={platform}>
          {label}
        </option>
      ))}
    </select>
  );
}

function platformLabel(platform: TrackPlatform) {
  return platforms.find(([value]) => value === platform)?.[1] ?? platform;
}

function genreLabel(genre: MusicGenre) {
  return musicGenreLabel(genre);
}

function validateTrackUrl(platform: TrackPlatform, input: string): string | null {
  let url: URL;
  try {
    url = new URL(input.trim());
  } catch {
    return 'Escribe un enlace completo que empiece con https://.';
  }

  if (url.protocol !== 'https:') return 'El enlace debe ser seguro y empezar con https://.';
  const host = url.hostname.toLowerCase();
  const path = url.pathname.replace(/\/+$/, '');

  if (platform === 'youtube' && path.startsWith('/shorts/')) {
    return 'Los enlaces de YouTube Shorts no están permitidos. Usa el enlace oficial de la canción o video.';
  }

  const valid =
    platform === 'spotify'
      ? host === 'open.spotify.com' && /\/(?:intl-[a-z-]+\/)?track\/[A-Za-z0-9]+$/i.test(path)
      : platform === 'youtube'
        ? (host === 'youtu.be' && path.length > 1) ||
          ((host === 'youtube.com' || host === 'www.youtube.com' || host === 'music.youtube.com') &&
            path === '/watch' &&
            Boolean(url.searchParams.get('v')))
        : platform === 'apple_music'
          ? host === 'music.apple.com' && path.includes('/album/')
          : platform === 'soundcloud'
            ? (host === 'soundcloud.com' || host === 'www.soundcloud.com') &&
              path.split('/').filter(Boolean).length >= 2
            : platform === 'deezer'
              ? (host === 'deezer.com' || host === 'www.deezer.com') &&
                /\/(?:[a-z]{2}\/)?track\/\d+$/i.test(path)
              : (host === 'tidal.com' || host === 'www.tidal.com' || host === 'listen.tidal.com') &&
                /\/(?:browse\/)?track\/\d+$/i.test(path);

  return valid
    ? null
    : 'Ese enlace no corresponde a una canción válida de la plataforma seleccionada.';
}
